import * as React from 'react';

import { KSUID } from '@owpz/ksuid';
import { cn } from '@rematter/ui-web';
import { useSetAtom } from 'jotai';
import { Calendar, Globe, Zap } from 'lucide-react';

import {
  $editorNodes,
  $createAutomationTriggerType,
  $hasUnsavedChanges,
} from '../../_state/automationEditor.state';
import { useTranslations } from '../../i18n/useTranslations';
import { TriggerType } from '../../types';

import { NodeType } from './nodes/types';

const triggerTypeToNodeType: Record<TriggerType, NodeType> = {
  BusinessEvent: 'BusinessEventTrigger',
  HttpWebhook: 'HttpWebhookTrigger',
  Schedule: 'ScheduleTrigger',
};

type TriggerOption = {
  type: TriggerType;
  icon: React.ReactNode;
};

const triggerOptions: TriggerOption[] = [
  {
    type: 'BusinessEvent',
    icon: <Zap className="size-8 text-primary" />,
  },
  {
    type: 'HttpWebhook',
    icon: <Globe className="size-8 text-primary" />,
  },
  {
    type: 'Schedule',
    icon: <Calendar className="size-8 text-primary" />,
  },
];

type TriggerSelectionPanelProps = {
  className?: string;
};

export const TriggerSelectionPanel: React.FC<TriggerSelectionPanelProps> = ({
  className,
}) => {
  const t = useTranslations();
  const setEditorNodes = useSetAtom($editorNodes);
  const setTriggerType = useSetAtom($createAutomationTriggerType);
  const setHasUnsavedChanges = useSetAtom($hasUnsavedChanges);

  const handleSelectTrigger = React.useCallback(
    (triggerType: TriggerType) => {
      const nodeType = triggerTypeToNodeType[triggerType];
      const label =
        t.editor.create?.triggerTypes?.[
          triggerType === 'BusinessEvent'
            ? 'businessEvent'
            : triggerType === 'HttpWebhook'
              ? 'httpWebhook'
              : 'schedule'
        ]?.label ?? t.triggerType[triggerType];

      const triggerNode = {
        id: `node_${KSUID.random()}`,
        type: nodeType,
        position: { x: 250, y: 100 },
        data: {
          label,
          config: {},
        },
      };

      setEditorNodes([triggerNode]);
      setTriggerType(triggerType);
      setHasUnsavedChanges(true);
    },
    [t, setEditorNodes, setTriggerType, setHasUnsavedChanges],
  );

  return (
    <div
      className={cn(
        '@TriggerSelectionPanel',
        'absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm',
        className,
      )}
    >
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-neutral">
          {t.editor.create?.title ?? 'Create New Automation'}
        </h2>
        <p className="mt-2 text-neutral/60">
          {t.editor.create?.selectTrigger ??
            'Select a trigger to start building your automation'}
        </p>
      </div>

      <div className="mt-8 flex gap-6">
        {triggerOptions.map((option) => {
          const key =
            option.type === 'BusinessEvent'
              ? 'businessEvent'
              : option.type === 'HttpWebhook'
                ? 'httpWebhook'
                : 'schedule';

          const label =
            t.editor.create?.triggerTypes?.[key]?.label ??
            t.triggerType[option.type];
          const description =
            t.editor.create?.triggerTypes?.[key]?.description ?? '';

          return (
            <button
              key={option.type}
              type="button"
              onClick={() => handleSelectTrigger(option.type)}
              className={cn(
                'group flex w-52 flex-col items-center gap-4 rounded-xl border-2 border-neutral/20 bg-white px-6 py-8',
                'transition-all duration-200',
                'hover:border-primary hover:shadow-lg hover:shadow-primary/10',
                'focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-none',
              )}
            >
              <div className="flex size-16 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/20">
                {option.icon}
              </div>
              <div className="text-center">
                <div className="font-semibold text-neutral">{label}</div>
                <div className="mt-1 text-sm text-neutral/60">
                  {description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
